/**
 * NestedSubtask Component
 * Unified component for nested subtasks with configurable display variants
 * Supports both 'row' (detailed) and 'list' (compact) views
 */
import React, { useState, useCallback } from 'react';
import {
  Button,
  Box,
  Typography,
  Chip,
  Collapse
} from '@mui/material';
import {
  AccountTree,
  KeyboardArrowDown,
  HourglassEmpty
} from '@mui/icons-material';
import { useDispatch } from 'react-redux';
import { fetchSubtasks } from '../../store/ticketSlice';
import { COLORS } from '../../shared/colors';
import { ENDPOINTS } from '../../services/endpoints';
import { minutesToTimeString } from '../../shared/timeUtils';
import { estimateTimeStyles } from '../../shared/globalSX';
import TaskCompletionCheckbox from './TaskCompletionCheckbox';
import OverflowTooltip from '../../shared/OverflowTooltip';
import AssigneeAvatarStack from '../../shared/AssigneeAvatarStack';
import { getPriorityColor } from '../../shared/commonFunctions';

const NestedSubtask = ({ subtask, level = 0, variant = 'list' }) => {
  const dispatch = useDispatch();
  const [isExpanded, setIsExpanded] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [nestedSubtasks, setNestedSubtasks] = useState([]);

  const handleSubtaskCompletionChange = async (taskId, completed) => {
    try {
      // Optimistically update UI
      setNestedSubtasks(prev => prev.map(task => 
        task.id === taskId ? { ...task, marked_as_completed: completed } : task
      ));
      // Note: Actual API call is handled by TaskCompletionCheckbox
    } catch (error) {
      console.error('Subtask completion update failed:', error);
      // Revert optimistic update
      setNestedSubtasks(prev => prev.map(task => 
        task.id === taskId ? { ...task, marked_as_completed: !completed } : task
      ));
    }
  };

  const isRowVariant = variant === 'row';


  const formatDate = (dateString) => {
    if (!dateString) return 'No due date';
    return dateString.split('T')[0];
  };

  const handleToggleNested = useCallback(async (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (!isExpanded && nestedSubtasks.length === 0) {
      setIsLoading(true);
      try {
        const result = await dispatch(fetchSubtasks(subtask.id)).unwrap();
        setNestedSubtasks(result);
      } catch (error) {
        console.error('Failed to fetch nested subtasks:', error);
      } finally {
        setIsLoading(false);
      }
    }
    setIsExpanded(!isExpanded);
  }, [dispatch, subtask.id, isExpanded, nestedSubtasks.length]);

  const containerStyles = isRowVariant ? {
    p: 1.5,
    mb: 1,
    bgcolor: level > 0 ? COLORS.blue[15] : COLORS.white,
    borderRadius: 2,
    border: `1px solid ${level > 0 ? COLORS.blue[100] : COLORS.gray[200]}`,
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    '&:hover': {
      bgcolor: level > 0 ? COLORS.blue[25] : COLORS.blue[25],
      borderColor: COLORS.blue[200],
      transform: 'translateY(-1px)',
      boxShadow: '0 2px 8px rgba(59, 130, 246, 0.1)'
    }
  } : {
    p: 1,
    mb: 0.5,
    bgcolor: level > 0 ? COLORS.blue[25] : COLORS.gray[25],
    borderRadius: 1,
    cursor: 'pointer',
    border: `1px solid ${level > 0 ? COLORS.blue[100] : COLORS.gray[100]}`,
    '&:hover': {
      bgcolor: level > 0 ? COLORS.blue[50] : COLORS.gray[50]
    }
  };

  return (
    <Box sx={{ ml: level * (isRowVariant ? 2 : 1) }}>
      <Box
        onClick={(e) => {
          e.stopPropagation();
          window.open(`/${ENDPOINTS.TASK_URL(subtask.ticket_number)}`, '_blank');
        }}
        sx={containerStyles}
      >
        <Box display="flex" alignItems="center" justifyContent="space-between" mb={isRowVariant ? 1 : 0.5} sx={{ overflow: 'hidden' }}>
          <Box display="flex" alignItems="center" gap={1} sx={{ flex: 1, minWidth: 0, overflow: 'hidden' }}>
            <TaskCompletionCheckbox 
              task={{
                id: subtask.id,
                completed: subtask.marked_as_completed || subtask.completed || subtask.is_completed || false
              }} 
              size="small"
              onStatusChange={handleSubtaskCompletionChange}
            />
            {isRowVariant && <AccountTree sx={{ fontSize: 14, color: COLORS.blue[500] }} />}
            <OverflowTooltip
              title={subtask.title}
              tooltipWidth={250}
              maxLines={2}
              sx={{
                fontSize: isRowVariant ? '14px' : '12px',
                fontWeight: 500,
                color: COLORS.text.primary,
                flex: 1,
                mr: isRowVariant ? 0 : 1,
                minWidth: 0
              }}
            />
          </Box>
          <Chip
            label={subtask.ticket_number}
            size="small"
            sx={{
              bgcolor: isRowVariant ? COLORS.blue[50] : COLORS.gray[100],
              color: isRowVariant ? COLORS.blue[700] : COLORS.gray[600],
              fontSize: isRowVariant ? '9px' : '8px',
              fontWeight: 600,
              height: isRowVariant ? 20 : 14,
              border: isRowVariant ? `1px solid ${COLORS.blue[200]}` : 'none',
              flexShrink: 0
            }}
          />
        </Box>
        <Box display="flex" alignItems="center" justifyContent="space-between" sx={{ overflow: 'hidden' }}>
          <Box display="flex" alignItems="center" gap={isRowVariant ? 1 : 0.5} sx={{ minWidth: 0, flex: 1, overflow: 'hidden' }}>
            <Box sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 0.4,
              px: 0.75,
              py: 0.2,
              borderRadius: '999px',
              border: `1px solid ${getPriorityColor(subtask.priority)}`,
              color: getPriorityColor(subtask.priority),
              fontSize: isRowVariant ? '10px' : '9px',
              fontWeight: 500,
              whiteSpace: 'nowrap',
              lineHeight: 1.4,
              flexShrink: 0
            }}>
              {typeof subtask.priority === 'object' ? subtask.priority.name : subtask.priority}
            </Box>

            {(subtask.estimate_time_in_minutes || subtask.estimateTimeInMinutes) && (
              <Box display="flex" alignItems="center" gap={0.5}>
                <HourglassEmpty sx={estimateTimeStyles.icon(isRowVariant)} />
                <Typography sx={estimateTimeStyles.text(isRowVariant)}>
                  {minutesToTimeString(subtask.estimate_time_in_minutes || subtask.estimateTimeInMinutes)}
                </Typography>
              </Box>
            )}
            {subtask.subtask_count > 0 && (
              <Button
                size="small"
                onClick={handleToggleNested}
                disabled={isLoading}
                startIcon={<AccountTree sx={{ fontSize: isRowVariant ? 12 : 10 }} />}
                endIcon={
                  <KeyboardArrowDown 
                    sx={{ 
                      fontSize: isRowVariant ? 14 : 12, 
                      transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                      transition: 'transform 0.2s ease-in-out'
                    }} 
                  />
                }
                sx={{
                  fontSize: isRowVariant ? '11px' : '10px',
                  fontWeight: 600,
                  color: COLORS.blue[700],
                  bgcolor: 'linear-gradient(135deg, #dbeafe 0%, #bfdbfe 100%)',
                  border: `1px solid ${COLORS.blue[300]}`,
                  borderRadius: isRowVariant ? 1.5 : 1,
                  textTransform: 'none',
                  minHeight: 'auto',
                  py: isRowVariant ? 0.75 : 0.5,
                  px: isRowVariant ? 1.5 : 1,
                  minWidth: 'auto',
                  boxShadow: isRowVariant ? '0 2px 4px rgba(59, 130, 246, 0.2)' : '0 1px 3px rgba(59, 130, 246, 0.2)',
                  '&:hover': {
                    bgcolor: COLORS.blue[100],
                    borderColor: COLORS.blue[400],
                    boxShadow: isRowVariant ? '0 4px 8px rgba(59, 130, 246, 0.3)' : '0 2px 6px rgba(59, 130, 246, 0.3)',
                    transform: isRowVariant ? 'translateY(-2px)' : 'translateY(-1px)'
                  },
                  '&:active': {
                    transform: 'translateY(0px)',
                    boxShadow: isRowVariant ? '0 2px 4px rgba(59, 130, 246, 0.2)' : '0 1px 2px rgba(59, 130, 246, 0.2)'
                  }
                }}
              >
                {isLoading ? 'Loading...' : (isRowVariant ? `${subtask.subtask_count} Subtasks` : `${subtask.subtask_count}`)}
              </Button>
            )}
            {isRowVariant && (
              <AssigneeAvatarStack
                assignees={subtask.assignees?.length > 0 ? subtask.assignees : (subtask.assignee ? [subtask.assignee] : [])}
                size={20}
                max={3}
              />
            )}
            {!isRowVariant && (
              <AssigneeAvatarStack
                assignees={subtask.assignees?.length > 0 ? subtask.assignees : (subtask.assignee ? [subtask.assignee] : [])}
                size={18}
                max={3}
              />
            )}
          </Box>
          {isRowVariant && (
            <Typography
              sx={{
                fontSize: '10px',
                color: COLORS.gray[500],
                fontWeight: 500
              }}
            >
              {formatDate(subtask.due_date)}
            </Typography>
          )}
        </Box>
      </Box>

      {subtask.subtask_count > 0 && (
        <Collapse in={isExpanded} timeout={300}>
          <Box sx={{ mt: 0.5, ml: 1 }}>
            {nestedSubtasks.map((nestedSubtask) => (
              <NestedSubtask 
                key={nestedSubtask.id} 
                subtask={nestedSubtask} 
                level={level + 1}
                variant={variant}
              />
            ))}
          </Box>
        </Collapse>
      )}
    </Box>
  );
};

export default NestedSubtask;